import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Inbox from './pages/Inbox';
import PostScheduler from './pages/PostScheduler';
import Influencers from './pages/Influencers';
import Pricing from './pages/Pricing';
import SignIn from './pages/SignIn';
import RequestAccess from './pages/RequestAccess';
import InstagramCallback from './pages/auth/instagram/callback';
import AuthCallback from './pages/auth/callback';
import { useAuth } from './hooks/useAuth';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { loading, setLoading } = useAuth();

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [setLoading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/sign-in" 
          element={
            <PublicRoute>
              <SignIn />
            </PublicRoute>
          }
        />
        <Route 
          path="/request-access" 
          element={
            <PublicRoute>
              <RequestAccess />
            </PublicRoute>
          }
        />
        <Route
          path="/pricing"
          element={<Layout><Pricing /></Layout>}
        />
        <Route
          path="/auth/instagram/callback"
          element={<InstagramCallback />}
        />
        <Route
          path="/auth/callback"
          element={<AuthCallback />}
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inbox"
          element={
            <ProtectedRoute>
              <Layout><Inbox /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inbox/:username"
          element={
            <ProtectedRoute>
              <Layout><Inbox /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scheduler"
          element={
            <ProtectedRoute>
              <Layout><PostScheduler /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/influencers"
          element={
            <ProtectedRoute>
              <Layout><Influencers /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}